import { MembershipIncomingRenewalPayload } from '@models/membership/MembershipIncomingRenewal'
import { MembershipTransactionPayload } from '@models/membership/MembershipTransaction'

/**
 * Dev fakes for the membership analytics surfaces the mock interceptor could not
 * cover: the two paginated lists and the `analytics/statistics` kinds. They
 * return the **payload** shapes the endpoints serve (the shared payment and
 * subscription resources), not the flat rows - the services still do the
 * flattening, so the mocked path exercises the same mapping as production.
 */

const MEMBERS = [
  ['Julien', 'Moreau'],
  ['Sophie', 'Lefebvre'],
  ['Marc', 'Dubois'],
  ['Alice', 'Martin'],
  ['Thomas', 'Bernard'],
  ['Lucie', 'Roux'],
  ['Pierre', 'Leroy'],
  ['Emma', 'Petit'],
  ['Nicolas', 'Simon'],
  ['Chloe', 'Michel'],
]

const PLANS = [
  { id: 1, name: 'Paris - Solo', price: 2500 },
  { id: 2, name: 'Paris - Duo', price: 4500 },
  { id: 3, name: 'Lyon - Solo', price: 1500 },
]

const COUPONS = [
  null,
  { id: 1, code: 'WELCOME', fixedAmount: 500, fullAmount: false },
  { id: 2, code: 'FOUNDER', fixedAmount: 1000, fullAmount: false },
]

// Every value the API can send, `unknown` and a bare null included.
const PAYMENT_METHODS = [
  'card',
  'direct_debit',
  'bank_transfer',
  'missing',
  'unknown',
  null,
  'card',
] as const

const PAST_OUTCOMES = [
  'renewed',
  'payment_failed',
  'cancelled',
  'awaiting_transfer',
] as const
const FUTURE_OUTCOMES = ['upcoming', 'upcoming', 'awaiting_transfer'] as const

interface FakeListQuery {
  startDate?: string
  endDate?: string
  pageSize?: number
  /** `filters[outcome][]`: only rows with one of these outcomes. */
  outcome?: string[]
}

/** Deterministic per index, so a reload does not reshuffle every row. */
const pick = <T>(list: T[], index: number): T => list[index % list.length]

const isoDaysAgo = (days: number): string =>
  new Date(Date.now() - days * 86400000).toISOString()

const fakeMember = (index: number) => {
  const [firstName, lastName] = pick(MEMBERS, index)

  return {
    id: index + 1,
    firstName,
    lastName,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@seed.test`,
    locale: 'fr',
    code: `M-${1000 + index}`,
    isActive: true,
    createdAt: isoDaysAgo(300),
    profilePictureUrl: undefined,
  }
}

const fakePlan = (index: number) => {
  const plan = pick(PLANS, index)

  return {
    id: plan.id,
    price: plan.price,
    en: { name: plan.name },
    fr: { name: plan.name },
  }
}

/** `subscription_payments` rows, three settlement states cycling. */
const fakeTransactionPayloads = ({ pageSize }: FakeListQuery = {}) =>
  Array.from({ length: Math.min(pageSize ?? 12, 12) }, (_, index) => {
    const plan = fakePlan(index)
    const coupon = pick(COUPONS, index)
    const settled = index % 3

    return {
      id: `01a00f1d-99de-73a1-8c17-95bc867800${String(index).padStart(2, '0')}`,
      amount: coupon ? plan.price - (coupon.fixedAmount ?? 0) : plan.price,
      // Price before the coupon, so the list price renders (struck through
      // when a coupon applied).
      amountBeforeDiscount: plan.price,
      pspReference: `SEED-${1800 + index}`,
      pspUrl: undefined,
      paymentMethod: index % 4 === 0 ? null : 'card',
      createdAt: isoDaysAgo(index + 1),
      completedAt: settled === 0 ? isoDaysAgo(index) : undefined,
      failedAt: settled === 1 ? isoDaysAgo(index) : undefined,
      subscription: {
        id: index + 1,
        plan,
        coupon: coupon ?? undefined,
        membershipProfiles: [fakeMember(index)],
      },
    } as unknown as MembershipTransactionPayload
  })

const DAY = 86400000

/**
 * Active `subscriptions` due to renew. Honours the query's date range and page
 * size (the previews ask for "next 7 days, 10 rows"), spreading the rows over
 * the window; with no range, from 15 days ago to 60 days ahead like the
 * drill-down's unfiltered list.
 */
const fakeIncomingRenewalPayloads = ({
  startDate,
  endDate,
  pageSize,
  outcome,
}: FakeListQuery = {}) => {
  const count = Math.min(pageSize ?? 12, 12)
  const start = startDate
    ? new Date(startDate).getTime()
    : Date.now() - 15 * DAY
  const end = endDate ? new Date(endDate).getTime() : Date.now() + 60 * DAY
  const today = new Date().setHours(0, 0, 0, 0)

  return Array.from({ length: count }, (_, index) => {
    const plan = fakePlan(index)
    const coupon = pick(COUPONS, index)
    const due = start + (count > 1 ? (index * (end - start)) / (count - 1) : 0)
    const isPast = due < today
    const withinNext30 = due >= today && due <= today + 30 * DAY

    return {
      id: index + 1,
      renewalDate: new Date(due).toISOString(),
      // `missing` is only ever computed for the next 30 days.
      paymentMethod:
        PAYMENT_METHODS[index % PAYMENT_METHODS.length] === 'missing' &&
        !withinNext30
          ? 'card'
          : PAYMENT_METHODS[index % PAYMENT_METHODS.length],
      outcome: outcome?.length
        ? outcome[index % outcome.length]
        : isPast
          ? PAST_OUTCOMES[index % PAST_OUTCOMES.length]
          : FUTURE_OUTCOMES[index % FUTURE_OUTCOMES.length],
      nextPaymentAmount: coupon
        ? plan.price - (coupon.fixedAmount ?? 0)
        : plan.price,
      plan,
      coupon: coupon ?? undefined,
      membershipProfiles: [fakeMember(index)],
    } as unknown as MembershipIncomingRenewalPayload
  })
}

const delta = (value: number, deltaPercent?: number) => ({
  value,
  deltaPercent,
})

const EURO = { isoCode: 'EUR', symbol: '€' }

/**
 * `analytics/statistics?entity=membership` per kind. Shapes mirror the
 * `Response*AnalyticsStatistics` models (the shared API contract), so a missing
 * kind returns `{}` rather than a wrong shape.
 */
const fakeMembershipStatistics = (
  kind: string,
  { endDate }: { endDate?: string } = {}
): Record<string, unknown> => {
  switch (kind) {
    case 'membership-club-today':
      return {
        currency: EURO,
        activeSubscriptions: delta(1356),
        activeMembers: delta(2332),
        arr: delta(5274800),
        revenuePerSubscription: delta(3890),
        averageLtv: delta(5400),
        averageYearsPaid: delta(1.4),
        expectedYears: delta(7.7),
        toCancel: delta(41),
        toCancelArr: delta(159500),
        toCancelRate: 3,
      }
    case 'membership-period':
      return {
        currency: EURO,
        collected: delta(4725000, 12),
        collectedNew: delta(1102000, 9),
        collectedRenewals: delta(3623000, 13),
        newMembers: delta(245, 8.9),
        departures: delta(54, 4.2),
        voluntaryDepartures: delta(31),
        involuntaryDepartures: delta(23),
        netGrowth: { value: 191, deltaValue: 38 },
        renewalsShare: 77,
        voluntaryShare: 57,
        netGrowthRate: 16,
      }
    case 'membership-attention':
      return {
        currency: EURO,
        failedRenewals: {
          count: 8,
          amount: 31200,
          oldestDueDate: new Date(Date.now() - 41 * 86400000)
            .toISOString()
            .slice(0, 10),
        },
        stuckReviews: { count: 6, oldestDays: 9 },
        // Mixed case: a count, plus subscriptions Adyen could not answer for.
        withoutPaymentMethod: { count: 7, amount: 27200, unchecked: 2 },
        dormantRenewals: { count: 9, amount: 35000 },
        cardFailureRate90d: 29,
      }
    case 'membership-engagement':
      return {
        currency: EURO,
        engagedMembersRate: { value: 62, deltaValue: 3, deltaPercent: 3 },
        engagedMembers: delta(1446),
        activeMembers: delta(2332),
        reservationsPerMember: delta(3.4, 6),
        coversPerMember: delta(5.2),
        eventRegistrationsPerMember: delta(0.7, -4),
        eventRegistrations: delta(1600),
        eventAttendanceRate: { value: 74, deltaValue: 2, deltaPercent: 2 },
        eventCheckIns: delta(1184),
        eventRegistrationsHeld: delta(1600),
        renewalRateEngaged: 93,
        renewalRateDormant: 54,
      }
    case 'membership-renewal-retention':
      return {
        currency: EURO,
        renewalRate: delta(87, 1.2),
        churn: delta(13, 2),
        due: delta(415),
        renewed: delta(361),
        notRenewed: delta(54),
        voluntary: delta(31),
        involuntary: delta(23),
        renewalRateByPaymentType: { card: 92, bank_transfer: 71 },
      }
    case 'membership-promotion':
      return {
        currency: EURO,
        discountsGranted: delta(248000, -4.2),
        couponsUsed: delta(47, 9),
        discountRate: delta(5.1),
        discountTrackedSince: '2026-08-17',
      }
    case 'membership-transactions':
      return {
        currency: EURO,
        totalCollected: delta(4725000, 12),
        transactionsCount: delta(342, 4),
        failedCount: delta(8, -2),
        failedRate: delta(2.3),
      }
    case 'membership-incoming-renewals':
      return {
        currency: EURO,
        totalToCollect: delta(1875000, 7),
        totalToRenew: delta(63, 3),
        withoutPaymentMethod: { count: 7, amount: 27200, unchecked: 0 },
        // Like the API: renewal rate / churn are omitted unless the range ends
        // before today.
        ...(endDate && endDate < new Date().toISOString().slice(0, 10)
          ? { renewalRate: delta(87, 1.2), churn: delta(13, 2) }
          : {}),
      }
    default:
      return {}
  }
}

export {
  type FakeListQuery,
  fakeIncomingRenewalPayloads,
  fakeMembershipStatistics,
  fakeTransactionPayloads,
}
